import type * as React from 'react';
import { cn } from '../../lib/cn.ts';
import type { ButtonType } from '../../lib/buttonType.ts';
import { CHAT_COLUMN_CLASS } from './chatColumn.ts';

export interface ChatQuickActionsProps extends Omit<
  React.ComponentProps<'div'>,
  'style'
> {
  /** Names the row for screen readers — "What you can ask here". */
  label: string;
  /** Must match the drawer's own `expanded`. */
  expanded?: boolean;
}

/**
 * The row of things worth asking on this page, sitting just above the composer.
 *
 * Above the input rather than inside it, and not a panel of its own: the chat
 * is one thing — a conversation — and these are ways to start it, not a second
 * surface beside it. They are at the bottom because that is where the hand
 * already is at the moment somebody decides to say something.
 *
 * The chips wrap: what does not fit beside the others drops onto the line
 * below. Not a sideways scroll — a scrollable row with no visible bar is a row
 * nobody scrolls — and nothing is ever dropped, because an action that
 * disappears because the window is narrow is not an action. Narrow therefore
 * becomes tall, which is affordable only because a page never offers more than
 * a handful.
 *
 * In full screen they line up against the right of the reading column, under
 * the composer's own edge, rather than spreading across it.
 */
export function ChatQuickActions({
  label,
  expanded = false,
  className,
  children,
  ...props
}: ChatQuickActionsProps) {
  return (
    <div
      data-slot='chat-quick-actions'
      role='group'
      aria-label={label}
      className={cn('shrink-0 px-4 py-2.5', className)}
      {...props}
    >
      <div
        className={cn(
          'flex flex-wrap gap-1.75',
          expanded && [CHAT_COLUMN_CLASS, 'justify-end'],
        )}
      >
        {children}
      </div>
    </div>
  );
}

export type ChatQuickActionProps = Omit<
  React.ComponentProps<'button'>,
  'style' | 'type'
> & {
  type?: ButtonType;
};

/**
 * One pill: an icon and the words that get sent when it is clicked.
 *
 * All of them the same grey on purpose — no green "suggested" one. A quick
 * action may some day run by itself, and a second pill still standing in green
 * beside the result would read as a contradiction (DEV-38).
 */
export function ChatQuickAction({
  className,
  type = 'button',
  ...props
}: ChatQuickActionProps) {
  return (
    <button
      data-slot='chat-quick-action'
      type={type}
      className={cn(
        'inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full',
        'px-3.25 py-1.75 text-sm font-medium whitespace-nowrap text-ink transition',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
        'disabled:pointer-events-none disabled:opacity-40',
        'bg-fill hover:bg-line [&_svg]:text-ink-muted',
        '[&_svg]:size-3.25 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}
